import React, { useState } from 'react';

import { Box, TabsV2, TabPanelV2 } from '@lp/ds-next';
import { useParams } from 'react-router-dom';

import LPPartnerAddressTable from './LPPartnerAddressesTable';
import { OrganizationTab } from './Tabs/Organization/OrganizationTab';
import PartnerSettings from './Tabs/PartnerSettings';
import WrapperAccountsTablePartner from './WrapperAccountsTablePartner';
import PartnerApiKeyTable from '../../apiKeys/components/PartnerApiKeyTable';
import ProductsTable from '../../products/components/ProductsTable';
import LPShopsTable from '../../shops/components/LPShopsTable';
import { ILPPartnerState } from '../api/useLPPartner';
import {
  IPartnerV3Update,
  PARTNER_BUSINESS_STATUS,
  PARTNER_BUSINESS_TYPE,
} from '../models/partnerV3';
import { usePartnerClosureRequest } from '@/features/gdpr/partnerClosure/api/getPartnerClosureRequest';
import { closureRequestStatuses } from '@/features/gdpr/partnerClosure/types';
import { useGetPartnerHierarchy } from '@/features/lp-village/partners/api/getPartnerHierarchy';
import { PartnerServiceLevel } from '@/features/serviceLevels';
import ArticlesProvider from '@/shared/providers/articlesProvider';
import ProductTotalProvider from '@/shared/providers/productTotalProvider';

const Tabs = ({
  partner,
  updatePartner,
}: {
  partner: ILPPartnerState;
  updatePartner: (data: IPartnerV3Update) => Promise<{ success: boolean }>;
}) => {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<number>(0);

  const canSyncWithERP =
    partner.data.business_information.type === PARTNER_BUSINESS_TYPE.PRO &&
    partner.data.business_information.status ===
      PARTNER_BUSINESS_STATUS.VALIDATED;

  const isOrganization = !!partner.data.parent_id;

  const { data: hierarchy } = useGetPartnerHierarchy(partner.data.id, {
    enabled: !!partner.data.id && !isOrganization,
  });
  const childrenCount = hierarchy?.children?.length ?? 0;

  const organizationLabel =
    childrenCount > 0 ? `Organizations (${childrenCount})` : 'Organizations';

  const allTabs = [
    {
      label: 'Addresses',
      key: 'addresses',
      children: (
        <LPPartnerAddressTable id={id} canSyncWithERP={canSyncWithERP} />
      ),
    },
    {
      label: 'Accounts',
      key: 'accounts',
      children: <WrapperAccountsTablePartner partnerId={id} />,
    },
    {
      label: 'Shops',
      key: 'shops',
      children: <LPShopsTable partnerId={id} />,
    },
    {
      label: 'Products',
      key: 'products',
      children: (
        <ProductTotalProvider>
          <ArticlesProvider>
            <ProductsTable partnerId={id} />
          </ArticlesProvider>
        </ProductTotalProvider>
      ),
    },
    {
      label: 'API Keys',
      key: 'api-keys',
      children: <PartnerApiKeyTable partnerID={id} />,
    },
    {
      label: 'Service Level',
      key: 'service-level',
      children: (
        <PartnerServiceLevel
          partner={partner.data}
          updatePartner={updatePartner}
        />
      ),
    },
    {
      label: 'Settings',
      key: 'settings',
      children: (
        <PartnerSettings partner={partner.data} updatePartner={updatePartner} />
      ),
    },
  ];

  if (!isOrganization) {
    allTabs.push({
      label: organizationLabel,
      key: 'organization',
      children: <OrganizationTab partner={partner.data} />,
    });
  }

  const { data: closureRequest } = usePartnerClosureRequest();
  const closedPartner =
    closureRequest?.status === closureRequestStatuses.Approved ||
    closureRequest?.status === closureRequestStatuses.Anonymized;

  return (
    <>
      <TabsV2 value={tab} setValue={setTab} tabs={allTabs} />
      <Box
        mt={1}
        style={{
          pointerEvents: closedPartner ? 'none' : 'auto',
        }}
      >
        {allTabs.map((tabItem, index) => (
          <TabPanelV2 key={index} value={tab} index={index}>
            {tabItem.children}
          </TabPanelV2>
        ))}
      </Box>
    </>
  );
};

export default Tabs;
